import { normalizeBibleHistoryPath, buildBibleHistoryPath } from './bibleHistoryPaths';

const API_URL = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';
const HISTORY_KEY = 'bible_navigation_history';
const POSITION_KEY = 'bible_history_position';
const MAX_HISTORY_ITEMS = 50;

/** @type {Promise<void> | null} */
let syncFromBackendInFlight = null;

class HistoryService {
  constructor() {
    /** Пропустить следующий saveHistory после goBack/goForward (кнопка Telegram и т.п.) */
    this._skipNextSave = false;
  }

  markProgrammaticNavigation() {
    this._skipNextSave = true;
  }

  consumeProgrammaticNavigation() {
    if (!this._skipNextSave) return false;
    this._skipNextSave = false;
    return true;
  }

  /** Выровнять позицию стека с текущим URL (после popstate и т.д.) */
  syncPositionToPath(url) {
    try {
      const normalized = normalizeBibleHistoryPath(url);
      const history = this.getHistory();
      const idx = history.findIndex(
        (item) => item?.url && normalizeBibleHistoryPath(item.url) === normalized
      );
      if (idx >= 0) {
        this.setPosition(idx);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  // Сохранить запись в историю (как раньше, но с бэкендом)
  async saveHistory(url, title = '', bookId = null, chapter = null, verse = null) {
    if (this.consumeProgrammaticNavigation()) {
      return;
    }
    try {
      const normalizedUrl = normalizeBibleHistoryPath(url);
      const segments = normalizedUrl.split('/').filter(Boolean);

      // Не добавляем в историю только список книг: /{translation}
      if (segments.length <= 1) {
        return;
      }

      // Сохраняем в localStorage (как раньше)
      const history = this.getHistory();
      const currentPosition = this.getPosition();
      
      // Удаляем дубликаты (сравниваем канонические пути — без ведущих нулей и без /parallel и т.д.)
      const currentItemUrl = history[currentPosition]?.url;
      if (
        history.length > 0 &&
        currentItemUrl &&
        normalizeBibleHistoryPath(currentItemUrl) === normalizedUrl
      ) {
        return;
      }
      
      // Обрезаем "будущее" если нужно
      let newHistory = history;
      if (currentPosition > 0) {
        newHistory = history.slice(currentPosition);
        this.setPosition(0);
      }
      
      // Добавляем новый элемент в начало
      const newItem = {
        url: normalizedUrl,
        title,
        timestamp: Date.now(),
        bookId,
        chapter,
        verse
      };
      
      newHistory.unshift(newItem);
      
      // Ограничиваем до MAX_HISTORY_ITEMS
      if (newHistory.length > MAX_HISTORY_ITEMS) {
        newHistory.splice(MAX_HISTORY_ITEMS);
      }
      
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));

      // Сохраняем на бэкенд (если авторизован)
      const authToken = localStorage.getItem('authToken');
      if (authToken && bookId) {
        try {
          const response = await fetch(`${API_URL}/history`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              bookId: parseInt(bookId),
              chapter: chapter ? parseInt(chapter) : null,
              verse: verse ? parseInt(verse) : null
            })
          });
          
          if (response.status === 401) {
            localStorage.removeItem('authToken');
            // Не редиректим здесь, так как это тихая операция
          }
        } catch (error) {
          // Тихая ошибка
          console.log('Backend history save error:', error);
        }
      }
    } catch (error) {
      console.error('History save error:', error);
    }
  }

  // Получить историю из localStorage (как раньше)
  getHistory() {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (!raw) return [];
      let history = JSON.parse(raw);
      if (!Array.isArray(history)) return [];

      let changed = false;
      history = history.map((item) => {
        if (!item || typeof item.url !== 'string') return item;
        const nu = normalizeBibleHistoryPath(item.url);
        if (nu !== item.url) changed = true;
        return { ...item, url: nu };
      });

      let pos = this.getPosition();
      if (history.length === 0) {
        if (pos !== 0) changed = true;
        pos = 0;
      } else if (pos >= history.length) {
        pos = history.length - 1;
        changed = true;
      }

      if (changed) {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
        this.setPosition(pos);
      }

      return history;
    } catch (error) {
      console.error('Error reading navigation history:', error);
      return [];
    }
  }

  getPosition() {
    try {
      const position = localStorage.getItem(POSITION_KEY);
      return position ? parseInt(position, 10) : 0;
    } catch (error) {
      return 0;
    }
  }

  setPosition(position) {
    try {
      localStorage.setItem(POSITION_KEY, position.toString());
    } catch (error) {
      console.error('Error saving position:', error);
    }
  }

  // Переход назад - возвращает URL или null
  goBack() {
    const history = this.getHistory();
    const currentPosition = this.getPosition();
    
    if (currentPosition < history.length - 1) {
      const newPosition = currentPosition + 1;
      this.setPosition(newPosition);
      this.markProgrammaticNavigation();
      return history[newPosition]?.url || null;
    }
    return null;
  }

  // Переход вперёд - возвращает URL или null
  goForward() {
    const currentPosition = this.getPosition();
    
    if (currentPosition > 0) {
      const newPosition = currentPosition - 1;
      this.setPosition(newPosition);
      this.markProgrammaticNavigation();
      const history = this.getHistory();
      return history[newPosition]?.url || null;
    }
    return null;
  }

  canGoBack() {
    const history = this.getHistory();
    const currentPosition = this.getPosition();
    return currentPosition < history.length - 1;
  }

  canGoForward() {
    const currentPosition = this.getPosition();
    return currentPosition > 0;
  }

  clearHistory() {
    try {
      localStorage.removeItem(HISTORY_KEY);
      localStorage.removeItem(POSITION_KEY);
    } catch (error) {
      console.error('Error clearing navigation history:', error);
    }
  }

  // Получить историю с бэкенда (для синхронизации)
  async getBackendHistory() {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        return [];
      }

      const response = await fetch(`${API_URL}/history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken');
        }
        return [];
      }

      return await response.json();
    } catch (error) {
      console.error('History get error:', error);
      return [];
    }
  }

  /**
   * Подтягивает историю с бэка и объединяет с localStorage (по каноническому URL берётся более свежий timestamp).
   * Перевод для URL берётся из bibleTranslation (как при чтении) — на сервере перевод не хранится.
   */
  async syncFromBackend() {
    if (syncFromBackendInFlight) return syncFromBackendInFlight;

    syncFromBackendInFlight = (async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) return;

        const items = await this.getBackendHistory();
        if (!Array.isArray(items) || items.length === 0) return;

        const translation =
          typeof localStorage !== 'undefined'
            ? localStorage.getItem('bibleTranslation') || 'kjv'
            : 'kjv';

        const fromServer = items
          .filter((row) => row && row.bookId != null)
          .map((row) => {
            const url = buildBibleHistoryPath({
              translation,
              bookId: row.bookId,
              chapter: row.chapter,
              verse: row.verse,
            });
            const rawAt = row.viewedAt ?? row.viewed_at;
            const ts = rawAt ? new Date(rawAt).getTime() : Date.now();
            return {
              url,
              title: '',
              timestamp: Number.isFinite(ts) ? ts : Date.now(),
              bookId: row.bookId,
              chapter: row.chapter ?? null,
              verse: row.verse ?? null,
            };
          });

        const local = this.getHistory();
        const byUrl = new Map();

        const put = (item) => {
          if (!item || typeof item.url !== 'string') return;
          const key = normalizeBibleHistoryPath(item.url);
          const prev = byUrl.get(key);
          const t = item.timestamp || 0;
          if (!prev || t > (prev.timestamp || 0)) {
            byUrl.set(key, { ...item, url: key });
          }
        };

        fromServer.forEach(put);
        local.forEach(put);

        const merged = Array.from(byUrl.values()).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        const capped = merged.slice(0, MAX_HISTORY_ITEMS);

        localStorage.setItem(HISTORY_KEY, JSON.stringify(capped));
        this.setPosition(0);
      } catch (error) {
        console.error('History syncFromBackend error:', error);
      } finally {
        syncFromBackendInFlight = null;
      }
    })();

    return syncFromBackendInFlight;
  }
}

export const historyService = new HistoryService();

